import type { StudentServiceUser } from '../api/studentService'
import type { User } from '@mfox/shared/contexts/UserContext'

/**
 * Maps a Student Service account onto the app's user. The backend has three
 * roles: STUDENT, ADMIN (with or without a college) and TEACHER, which is a
 * mentor.
 */
export function toAppUser(account: StudentServiceUser): User {
  // The server leaves collegeId out of the response when there is none.
  const collegeId = account.collegeId ?? null
  const base = {
    email: account.email,
    name: [account.firstName, account.lastName].filter(Boolean).join(' ') || undefined,
    firstName: account.firstName,
    lastName: account.lastName,
    bio: account.bio ?? null,
    collegeId: collegeId === null ? null : String(collegeId),
    studentId: null,
    skills: account.skills ?? [],
    aspiration: account.careerGoal ?? null,
    onboardingComplete: true,
  }

  // A student without a career goal hasn't onboarded yet; the app's onboarding gate sends them through it.
  if (account.role === 'STUDENT') return { ...base, role: 'student', collegeId: base.collegeId, onboardingComplete: !!account.careerGoal }
  if (account.role === 'ADMIN' && collegeId !== null) return { ...base, role: 'college-admin' }
  if (account.role === 'ADMIN') return { ...base, role: 'mfox', collegeId: null }
  if (account.role === 'TEACHER') return { ...base, role: 'mentor', collegeId: null, mentorId: account.mentorId ?? null }
  throw new Error('This account type does not have access to this app yet.')
}
